import { createPromiseClient } from '@connectrpc/connect'
import { PlaceDetailService } from '@fingertip/creator-proto/gen/fingertip/creator/place_detail/v1/place_detail_connect'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PlacesAutocomplete } from '@/components/shared/places-autocomplete'
import { ComboboxOption } from '@/components/ui/combobox'
import { transport } from '@/lib/connect'
import { useToken } from '@/lib/hooks/use-token'

import { InputField } from '../fields/input-field'
import { PhoneField } from '../fields/phone-field'

const detailClient = createPromiseClient(PlaceDetailService, transport)

type Props = {
  mode: 'billing' | 'shipping'
  defaultValues?: {
    name?: string
    phone?: string
    address1?: string
    address2?: string
    city?: string
    country?: string
    state?: string
    postalCode?: string
  }
  form: any
  onAddressChange?: (address: any) => void
  phoneRequired?: boolean
}

export const AddressForm = ({
  defaultValues,
  form,
  mode,
  onAddressChange,
  phoneRequired,
}: Props) => {
  const { t } = useTranslation()

  const { callOptions } = useToken()

  const [showMoreFields, setShowMoreFields] = useState(false)
  const country = form.watch(`${mode}.country`)

  useEffect(() => {
    if (onAddressChange && country) {
      onAddressChange({
        country,
      })
    }
  }, [country, onAddressChange])

  const handlePlaceSelect = useCallback(
    async (place: ComboboxOption | undefined) => {
      if (!place) return

      try {
        const result = await detailClient.getPlaceDetail(
          {
            googlePlaceId: String(place.id),
            addressOnly: true,
          },
          callOptions,
        )
        const details = JSON.parse(result.details)

        const addressComponents = details.address_components

        // Helper function to find component by type
        const getComponent = (type: string) =>
          addressComponents.find((component: any) =>
            component.types.includes(type),
          )

        // Build street address (combine street number and route)
        const streetNumber = getComponent('street_number')?.long_name || ''
        const route = getComponent('route')?.long_name || ''
        const address1 = `${streetNumber} ${route}`.trim()

        form.setValue(`${mode}.address1`, address1)
        form.setValue(`${mode}.city`, getComponent('locality')?.long_name || '')
        form.setValue(
          `${mode}.state`,
          getComponent('administrative_area_level_1')?.short_name || '',
        )
        form.setValue(
          `${mode}.postalCode`,
          getComponent('postal_code')?.long_name || '',
        )
        form.setValue(
          `${mode}.country`,
          getComponent('country')?.short_name || '',
        )

        form.trigger()

        setShowMoreFields(true)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching place details:', error)
      }
    },
    [form, mode, callOptions],
  )

  return (
    <div>
      <InputField
        control={form.control}
        name={`${mode}.name`}
        label={t('full_name')}
        placeholder={t('enter_full_name')}
      />

      <PlacesAutocomplete
        defaultValue={
          defaultValues?.address1
            ? {
                id: '',
                label: defaultValues.address1,
              }
            : undefined
        }
        onSelect={handlePlaceSelect}
        label={t('address')}
        hideAddress={showMoreFields}
        onRegionChange={(value) => form.setValue(`${mode}.country`, value.id)}
      />

      {showMoreFields && (
        <>
          <InputField
            control={form.control}
            name={`${mode}.address1`}
            label={t('address_line_1')}
          />

          <InputField
            control={form.control}
            name={`${mode}.address2`}
            label={t('address_line_2')}
            placeholder={t('apt_suite_unit')}
          />

          <InputField
            control={form.control}
            name={`${mode}.city`}
            label={t('city')}
          />

          <div className="grid grid-cols-2 gap-4">
            <InputField
              control={form.control}
              name={`${mode}.state`}
              label={t('state')}
            />

            <InputField
              control={form.control}
              name={`${mode}.postalCode`}
              label={t('postal_code')}
            />
          </div>

          <PhoneField
            control={form.control}
            name={`${mode}.phone`}
            label={
              phoneRequired ? t('phone_number') : t('phone_number_optional')
            }
            defaultCountry={country}
          />
        </>
      )}
    </div>
  )
}
